"use client";

import { useEffect } from "react";
import { createClient } from "@/lib/supabase/client";

const STORAGE_KEY = "pendingAuth";

export type PendingAuthMode = "login" | "signup";

export function setPendingAuth(mode: PendingAuthMode) {
  try {
    sessionStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // sessionStorage can throw in private browsing / restricted contexts.
    // The OAuth redirect should still proceed; we just lose the auto-reopen.
  }
}

export function consumePendingAuth(): PendingAuthMode | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (value !== "login" && value !== "signup") return null;
    sessionStorage.removeItem(STORAGE_KEY);
    return value;
  } catch {
    return null;
  }
}

export function clearPendingAuth() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Reopens the login or signup modal when the user lands back on this page
// after starting a Google/Facebook/LinkedIn OAuth flow.
//
// Strategy:
//   - OAuth buttons push `?login=1` or `?signup=1` to history before
//     redirecting. On Back the URL is preserved either way.
//   - Fresh-load Back: the home page's existing URL-param effect reads
//     ?login=1 / ?signup=1 on mount and opens the matching modal.
//   - bfcache restore: the URL is preserved but mount doesn't re-run,
//     so we force a hard reload from the pageshow handler.
//   - sessionStorage flag is consumed on mount as a defense-in-depth
//     fallback for browsers that drop the URL params somehow.
export function useAuthModalSync(
  _loginOpen: boolean,
  _signupOpen: boolean,
  setLoginOpen: (open: boolean) => void,
  setSignupOpen: (open: boolean) => void,
) {
  useEffect(() => {
    const pending = consumePendingAuth();
    if (pending === "login" || pending === "signup") {
      // OAuth success lands at /auth/callback → /projects, leaving the
      // flag in sessionStorage. When the user later opens Home the flag
      // is still here even though they're signed in — only re-show the
      // modal if there's no session (cancelled / Back from provider).
      const supabase = createClient();
      void supabase.auth.getUser().then(({ data }) => {
        if (data.user) {
          console.log("[auth] mount: skip modal reopen — already signed in", {
            pending,
          });
          return;
        }
        if (pending === "login") {
          console.log("[auth] mount: opening login modal from pendingAuth");
          setLoginOpen(true);
        } else {
          console.log("[auth] mount: opening signup modal from pendingAuth");
          setSignupOpen(true);
        }
      });
    }

    const onPageShow = (event: PageTransitionEvent) => {
      console.log("[auth] pageshow", { persisted: event.persisted });
      if (!event.persisted) return;
      // bfcache restore — React/Base UI state is stale. Force a fresh
      // load so the URL-param effect on mount can reopen the modal.
      window.location.reload();
    };

    const killBfcache = () => {};

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("unload", killBfcache);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("unload", killBfcache);
    };
  }, [setLoginOpen, setSignupOpen]);
}
